/**
 * SimulationAvatar - AI character avatar for immersive simulation experience.
 *
 * Shows an animated SVG avatar with emotion states (neutral, angry, happy, thinking, speaking).
 * The avatar "speaks" (mouth animation) when TTS is active.
 */

import React, { useEffect, useState } from 'react';

export type AvatarEmotion = 'neutral' | 'angry' | 'happy' | 'thinking' | 'speaking' | 'sad';

interface SimulationAvatarProps {
  emotion?: AvatarEmotion;
  name?: string;
  role?: string;
  size?: 'sm' | 'md' | 'lg';
  speaking?: boolean;
  className?: string;
}

const emotionColors: Record<AvatarEmotion, { bg: string; ring: string; accent: string }> = {
  neutral: { bg: '#6366f1', ring: '#818cf8', accent: '#4f46e5' },
  angry: { bg: '#dc2626', ring: '#ef4444', accent: '#991b1b' },
  happy: { bg: '#16a34a', ring: '#22c55e', accent: '#15803d' },
  thinking: { bg: '#7c3aed', ring: '#8b5cf6', accent: '#6d28d9' },
  speaking: { bg: '#2563eb', ring: '#3b82f6', accent: '#1d4ed8' },
  sad: { bg: '#64748b', ring: '#94a3b8', accent: '#475569' },
};

const sizeMap = {
  sm: { container: 'w-12 h-12', svg: 48 },
  md: { container: 'w-20 h-20', svg: 80 },
  lg: { container: 'w-32 h-32', svg: 128 },
};

const SimulationAvatar: React.FC<SimulationAvatarProps> = ({
  emotion = 'neutral',
  name,
  role,
  size = 'md',
  speaking = false,
  className = '',
}) => {
  const [mouthFrame, setMouthFrame] = useState(0);
  const effectiveEmotion: AvatarEmotion = speaking ? 'speaking' : emotion;
  const colors = emotionColors[effectiveEmotion];
  const dims = sizeMap[size];

  // Mouth animation when speaking
  useEffect(() => {
    if (!speaking) {
      setMouthFrame(0);
      return;
    }
    const interval = setInterval(() => {
      setMouthFrame((prev) => (prev + 1) % 4);
    }, 120);
    return () => clearInterval(interval);
  }, [speaking]);

  // Mouth shapes for speaking animation
  const mouthPaths = [
    'M 30 52 Q 40 54 50 52', // closed
    'M 30 51 Q 40 56 50 51', // slightly open
    'M 30 50 Q 40 58 50 50', // open
    'M 30 51 Q 40 55 50 51', // slightly open
  ];

  // Eye shapes based on emotion
  const renderEyes = () => {
    switch (effectiveEmotion) {
      case 'angry':
        return (
          <>
            {/* Angry eyebrows */}
            <line x1="22" y1="32" x2="32" y2="36" stroke={colors.accent} strokeWidth="2" strokeLinecap="round" />
            <line x1="58" y1="32" x2="48" y2="36" stroke={colors.accent} strokeWidth="2" strokeLinecap="round" />
            <ellipse cx="28" cy="40" rx="3" ry="2.5" fill="white" />
            <ellipse cx="52" cy="40" rx="3" ry="2.5" fill="white" />
            <circle cx="28" cy="40" r="1.5" fill="#1e293b" />
            <circle cx="52" cy="40" r="1.5" fill="#1e293b" />
          </>
        );
      case 'happy':
        return (
          <>
            {/* Happy curved eyes */}
            <path d="M 24 40 Q 28 36 32 40" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" />
            <path d="M 48 40 Q 52 36 56 40" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" />
          </>
        );
      case 'thinking':
        return (
          <>
            <ellipse cx="28" cy="40" rx="3" ry="3" fill="white" />
            <ellipse cx="52" cy="38" rx="3" ry="3" fill="white" />
            <circle cx="29" cy="41" r="1.5" fill="#1e293b" />
            <circle cx="53" cy="39" r="1.5" fill="#1e293b" />
            {/* One eyebrow raised */}
            <line x1="22" y1="33" x2="33" y2="34" stroke={colors.accent} strokeWidth="1.5" strokeLinecap="round" />
            <line x1="47" y1="31" x2="58" y2="33" stroke={colors.accent} strokeWidth="1.5" strokeLinecap="round" />
          </>
        );
      case 'sad':
        return (
          <>
            {/* Sad eyes */}
            <ellipse cx="28" cy="42" rx="3" ry="2.5" fill="white" />
            <ellipse cx="52" cy="42" rx="3" ry="2.5" fill="white" />
            <circle cx="28" cy="43" r="1.5" fill="#1e293b" />
            <circle cx="52" cy="43" r="1.5" fill="#1e293b" />
            {/* Sad eyebrows */}
            <line x1="23" y1="34" x2="33" y2="32" stroke={colors.accent} strokeWidth="1.5" strokeLinecap="round" />
            <line x1="47" y1="32" x2="57" y2="34" stroke={colors.accent} strokeWidth="1.5" strokeLinecap="round" />
          </>
        );
      default:
        return (
          <>
            <ellipse cx="28" cy="40" rx="3" ry="3" fill="white" />
            <ellipse cx="52" cy="40" rx="3" ry="3" fill="white" />
            <circle cx="28" cy="40" r="1.5" fill="#1e293b" />
            <circle cx="52" cy="40" r="1.5" fill="#1e293b" />
          </>
        );
    }
  };

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <div
        className={`${dims.container} rounded-full flex items-center justify-center relative transition-all duration-300`}
        style={{
          background: `radial-gradient(circle at 35% 30%, ${colors.ring}, ${colors.bg})`,
          boxShadow: speaking
            ? `0 0 20px ${colors.ring}, 0 0 40px ${colors.ring}40`
            : `0 4px 12px ${colors.accent}30`,
        }}
      >
        {/* Pulsing ring when speaking */}
        {speaking && (
          <div
            className="absolute inset-0 rounded-full animate-ping opacity-30"
            style={{ background: colors.ring, animationDuration: '1.5s' }}
          />
        )}

        <svg
          width={dims.svg * 0.8}
          height={dims.svg * 0.8}
          viewBox="0 0 80 80"
          fill="none"
          className="relative z-10"
        >
          {/* Face circle */}
          <circle cx="40" cy="40" r="36" fill={colors.bg} opacity="0.9" />

          {/* Hair / top */}
          <path
            d="M 10 35 Q 10 15 40 12 Q 70 15 70 35 Q 65 25 40 22 Q 15 25 10 35 Z"
            fill={colors.accent}
            opacity="0.6"
          />

          {/* Eyes */}
          {renderEyes()}

          {/* Nose */}
          <path d="M 40 42 L 38 48 L 42 48 Z" fill={colors.accent} opacity="0.3" />

          {/* Mouth */}
          <path
            d={speaking ? mouthPaths[mouthFrame] : emotion === 'happy' ? 'M 28 52 Q 40 58 52 52' : emotion === 'angry' ? 'M 28 54 Q 40 48 52 54' : emotion === 'sad' ? 'M 28 54 Q 40 50 52 54' : 'M 30 53 Q 40 55 50 53'}
            stroke="white"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
          />

          {/* Thinking indicator */}
          {effectiveEmotion === 'thinking' && (
            <g>
              <circle cx="62" cy="20" r="3" fill="white" opacity="0.8">
                <animate attributeName="opacity" values="0.3;1;0.3" dur="1.5s" repeatCount="indefinite" />
              </circle>
              <circle cx="68" cy="14" r="2" fill="white" opacity="0.6">
                <animate attributeName="opacity" values="0.2;0.8;0.2" dur="1.5s" begin="0.3s" repeatCount="indefinite" />
              </circle>
              <circle cx="72" cy="9" r="1.5" fill="white" opacity="0.4">
                <animate attributeName="opacity" values="0.1;0.6;0.1" dur="1.5s" begin="0.6s" repeatCount="indefinite" />
              </circle>
            </g>
          )}
        </svg>
      </div>

      {(name || role) && size !== 'sm' && (
        <div className="mt-2 text-center">
          {name && (
            <div className="text-sm font-semibold text-gray-900">{name}</div>
          )}
          {role && (
            <div className="text-xs text-gray-500">{role}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SimulationAvatar;
